<template>
  <modal :show="show" class="modal-scroll-fix white-content" @update:show="$emit('update:show', $event)">
    <div class="ppm-header">
      <h4 class="card-title mb-0">{{ title || $t('tools.pickProject') }}</h4>
      <p class="ppm-subtitle">{{ $t('projects.pickProjectSubtitle') }}</p>
    </div>

    <el-input
      v-model="searchQuery"
      clearable
      class="mb-3 search-input"
      :placeholder="$t('common.searchRecords')"
      @clear="onClearSearch"
    >
      <template #prefix>
        <el-icon class="el-input__icon"><search /></el-icon>
      </template>
    </el-input>

    <!-- Project list -->
    <div class="ppm-list">
      <template v-if="loading">
        <div v-for="n in 4" :key="'skeleton-' + n" class="ppm-item ppm-skeleton">
          <div class="ppm-avatar ppm-skeleton-block"></div>
          <div class="ppm-skeleton-lines">
            <div class="ppm-skeleton-block ppm-skeleton-line short"></div>
            <div class="ppm-skeleton-block ppm-skeleton-line"></div>
          </div>
        </div>
      </template>

      <template v-else-if="projects.length">
        <div
          v-for="project in projects"
          :key="project.id"
          class="ppm-item"
          :class="{ 'is-selected': selectedProjectId === project.id }"
        >
          <div class="ppm-row" @click="onRowClick(project)">
            <div class="ppm-avatar">
              <i class="material-icons">work_outline</i>
            </div>
            <div class="ppm-info">
              <div class="ppm-key">{{ project.key }}</div>
              <div v-if="project.name" class="ppm-name">{{ project.name }}</div>
              <div
                v-if="selectedProjectId === project.id && selectedPhase && expandedProjectId !== project.id"
                class="ppm-picked-phase"
              >
                <i class="material-icons">subdirectory_arrow_right</i>{{ selectedPhase.name }}
              </div>
            </div>
            <div class="ppm-side">
              <span v-if="hasPhases(project)" class="ppm-phase-badge">
                <i class="material-icons">account_tree</i>
                {{ project.phasesCount }}
              </span>
              <i v-if="selectedProjectId === project.id" class="material-icons ppm-check">check_circle</i>
              <button
                v-if="hasPhases(project)"
                type="button"
                class="ppm-chevron-btn"
                :class="{ 'is-open': expandedProjectId === project.id }"
                @click.stop="toggleExpand(project)"
              >
                <i class="material-icons">expand_more</i>
              </button>
            </div>
          </div>

          <!-- Phases drawer (animated open/close) -->
          <div v-if="hasPhases(project)" class="ppm-phases-wrap" :class="{ 'is-open': expandedProjectId === project.id }">
            <div class="ppm-phases">
              <div class="ppm-phases-inner">
                <div v-if="phasesLoading && expandedProjectId === project.id" class="ppm-phase ppm-phase-loading">
                  <span class="ppm-skeleton-block ppm-skeleton-line"></span>
                </div>
                <template v-else>
                  <div
                    v-if="!requiresPhase(project)"
                    class="ppm-whole-project"
                    :class="{ 'is-selected': selectedProjectId === project.id && selectedPhaseId === null }"
                    @click.stop="selectPhase(project, null)"
                  >
                    <span class="ppm-wp-icon"><i class="material-icons">work_outline</i></span>
                    <span class="ppm-phase-name">{{ $t('projects.wholeProject') }}</span>
                    <i v-if="selectedProjectId === project.id && selectedPhaseId === null" class="material-icons ppm-phase-check">check</i>
                  </div>
                  <div class="ppm-phase-divider">{{ $t('projects.phases') }}</div>
                  <div
                    v-for="phase in phasesFor(project.id)"
                    :key="phase.id"
                    class="ppm-phase"
                    :class="{ 'is-selected': selectedPhaseId === phase.id }"
                    @click.stop="selectPhase(project, phase)"
                  >
                    <span class="ppm-phase-dot"></span>
                    <span class="ppm-phase-name">{{ phase.name }}</span>
                    <i v-if="selectedPhaseId === phase.id" class="material-icons ppm-phase-check">check</i>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div v-else class="ppm-empty">
        <i class="material-icons">search_off</i>
        <p>{{ $t('projects.noProjectsFound') }}</p>
      </div>
    </div>

    <!-- Footer: pagination + confirm -->
    <div class="ppm-footer">
      <div class="ppm-pager">
        <button type="button" class="ppm-pager-btn" :disabled="currentPage === 1 || loading" @click="changePage(currentPage - 1)">
          <i class="material-icons">chevron_left</i>
        </button>
        <span class="ppm-pager-label">{{ pagerLabel }}</span>
        <button type="button" class="ppm-pager-btn" :disabled="currentPage >= totalPages || loading" @click="changePage(currentPage + 1)">
          <i class="material-icons">chevron_right</i>
        </button>
      </div>
      <material-button color="dark" fill :disabled="confirmDisabled" @click="confirm">
        {{ $t('common.confirm') }}
      </material-button>
    </div>
  </modal>
</template>

<script>
import { ElInput, ElIcon } from 'element-plus';
import { Search } from '@element-plus/icons-vue';
import Modal from './Modal.vue';
import MaterialButton from '../MaterialButton.vue';
import api from '../../services/api';
import { getCustomerConfig } from '../../views/Settings/customerConfigHelper';

// Self-contained project picker: fetches, searches and paginates projects itself and
// (when the EnableProjectPhases config is on) lets the user pick a phase of a project.
export default {
  components: { Modal, MaterialButton, ElInput, ElIcon, Search },
  props: {
    show: { type: Boolean, required: true },
    // Optional projectStatusId filter (e.g. 2 = Active)
    statusFilter: { type: Number, default: null },
    // 'toolReservation' | 'timeRegistration' — most recently used projects first
    orderBy: { type: String, default: null },
    // Fixed page size override; when null the page size adapts to the viewport
    // height so the list never needs to scroll.
    pageSize: { type: Number, default: null },
    title: { type: String, default: '' },
  },
  emits: ['update:show', 'confirm'],
  data() {
    return {
      projects: [],
      total: 0,
      currentPage: 1,
      searchQuery: '',
      loading: false,
      phasesEnabled: false,
      selectedProjectId: null,
      selectedProject: null,
      selectedPhaseId: null,
      selectedPhase: null,
      expandedProjectId: null,
      phasesLoading: false,
      phasesByProject: {},
      dynamicPageSize: 8,
    };
  },
  computed: {
    effectivePageSize() {
      return this.pageSize || this.dynamicPageSize;
    },
    totalPages() {
      return this.total > 0 ? Math.ceil(this.total / this.effectivePageSize) : 1;
    },
    pagerLabel() {
      if (this.total === 0) return '0';
      const from = (this.currentPage - 1) * this.effectivePageSize + 1;
      const to = Math.min(this.total, this.currentPage * this.effectivePageSize);
      return `${from}–${to} ${this.$t('pagination.of')} ${this.total}`;
    },
    confirmDisabled() {
      if (!this.selectedProjectId) return true;
      // Phase-mandatory project: a phase must be picked before confirming
      return this.selectedProject && this.requiresPhase(this.selectedProject) && this.selectedPhaseId === null;
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.searchQuery = '';
        this.currentPage = 1;
        this.selectedProjectId = null;
        this.selectedProject = null;
        this.selectedPhaseId = null;
        this.selectedPhase = null;
        this.expandedProjectId = null;
        this.computePageSize();
        this.fetchProjects();
      }
    },
    searchQuery() {
      clearTimeout(this._searchDebounce);
      this._searchDebounce = setTimeout(() => {
        this.currentPage = 1;
        this.fetchProjects();
      }, 400);
    },
  },
  async created() {
    this.computePageSize();
    this.phasesEnabled = await getCustomerConfig('EnableProjectPhases') === true;
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this._resizeDebounce);
    clearTimeout(this._searchDebounce);
  },
  methods: {
    // Fit the page to the viewport so the list never scrolls: subtract the modal
    // chrome (margins, header, search box, footer) and divide by the card height.
    computePageSize() {
      const chrome = 290;
      const cardHeight = 70; // 60px min-height + 10px gap
      const fit = Math.floor((window.innerHeight - chrome) / cardHeight);
      this.dynamicPageSize = Math.min(15, Math.max(3, fit));
    },
    onResize() {
      if (!this.show || this.pageSize) return;
      clearTimeout(this._resizeDebounce);
      this._resizeDebounce = setTimeout(() => {
        const before = this.dynamicPageSize;
        this.computePageSize();
        if (this.dynamicPageSize !== before) {
          this.currentPage = 1;
          this.fetchProjects();
        }
      }, 250);
    },
    hasPhases(project) {
      return this.phasesEnabled && project.phasesCount > 0;
    },
    // ERP flag: registrations must go on a phase, not the project itself. Only
    // enforced when we actually have phases for the project — otherwise it'd be
    // impossible to register anything on it.
    requiresPhase(project) {
      return project.phaseRequired === true && this.hasPhases(project);
    },
    phasesFor(projectId) {
      return this.phasesByProject[projectId] || [];
    },
    fetchProjects() {
      this.loading = true;
      let url = `/Project?currentPage=${this.currentPage}&pageSize=${this.effectivePageSize}`;
      if (this.searchQuery) url += `&searchString=${encodeURIComponent(this.searchQuery)}`;
      if (this.statusFilter !== null) url += `&projectStatusId=${this.statusFilter}`;
      if (this.orderBy === 'toolReservation') url += '&orderByLatestUsedInToolReservation=true';
      if (this.orderBy === 'timeRegistration') url += '&orderByLatestUsedInTimeRegistration=true';

      api.get(url)
        .then(response => {
          this.projects = response.data.result;
          this.total = response.data.totalItems;
          this.expandedProjectId = null;
          this.loading = false;
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.loading = false;
          this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 2000 });
        });
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.fetchProjects();
    },
    onClearSearch() {
      clearTimeout(this._searchDebounce);
      this.searchQuery = '';
      this.currentPage = 1;
      this.fetchProjects();
    },
    onRowClick(project) {
      if (this.selectedProjectId === project.id) {
        // Second click on the selected project toggles its phases drawer
        if (this.hasPhases(project)) this.toggleExpand(project);
        return;
      }
      this.selectProject(project);
    },
    async toggleExpand(project) {
      if (this.expandedProjectId === project.id) {
        this.expandedProjectId = null;
        return;
      }
      this.expandedProjectId = project.id;
      await this.ensurePhasesLoaded(project.id);
    },
    async ensurePhasesLoaded(projectId) {
      if (this.phasesByProject[projectId]) return;
      this.phasesLoading = true;
      try {
        const response = await api.get(`/Project/${projectId}/Phases`);
        this.phasesByProject[projectId] = response.data;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.phasesByProject[projectId] = [];
      }
      this.phasesLoading = false;
    },
    async selectProject(project) {
      this.selectedProjectId = project.id;
      this.selectedProject = project;
      this.selectedPhaseId = null;
      this.selectedPhase = null;

      if (this.hasPhases(project)) {
        this.expandedProjectId = project.id;
        await this.ensurePhasesLoaded(project.id);
      } else {
        this.expandedProjectId = null;
      }
    },
    selectPhase(project, phase) {
      this.selectedProjectId = project.id;
      this.selectedProject = project;
      this.selectedPhaseId = phase ? phase.id : null;
      this.selectedPhase = phase;
    },
    confirm() {
      if (this.confirmDisabled) return;
      this.$emit('confirm', {
        projectId: this.selectedProjectId,
        projectKey: this.selectedProject?.key,
        projectName: this.selectedProject?.name,
        phaseId: this.selectedPhaseId,
        phaseName: this.selectedPhase?.name || null,
      });
    },
  },
};
</script>

<style scoped>
.modal-scroll-fix {
  overflow-y: auto;
}

.ppm-header {
  margin-bottom: 12px;
}
.ppm-subtitle {
  margin: 2px 0 0;
  font-size: 0.8rem;
  color: #7b809a;
}

.ppm-list {
  /* No inner scrolling — the page size is computed to fit the viewport. */
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 4px 14px 4px;
}

.ppm-item {
  /* flex-shrink: 0 is load-bearing — without it the cards compress to fit the
     list's max-height when a phases drawer opens, squashing every other row. */
  flex: 0 0 auto;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  overflow: hidden;
}
.ppm-item:hover {
  border-color: #c8ccd4;
  box-shadow: 0 2px 8px rgba(52, 71, 103, 0.08);
}
.ppm-item.is-selected {
  border-color: #344767;
  box-shadow: 0 2px 10px rgba(52, 71, 103, 0.16);
}

.ppm-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 60px;
  padding: 10px 12px 10px 14px;
  cursor: pointer;
}

.ppm-avatar {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #42424a, #191919);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ppm-avatar .material-icons {
  font-size: 20px;
}
.ppm-item.is-selected .ppm-avatar {
  background: linear-gradient(135deg, #3e5476, #344767);
}

.ppm-info {
  flex: 1 1 auto;
  min-width: 0;
}
.ppm-key {
  font-size: 0.875rem;
  font-weight: 700;
  color: #344767;
  line-height: 1.3;
  word-break: break-word;
}
.ppm-name {
  font-size: 0.8rem;
  color: #67748e;
  line-height: 1.35;
  word-break: break-word;
}
.ppm-picked-phase {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: 2px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #344767;
}
.ppm-picked-phase .material-icons {
  font-size: 14px;
  color: #7b809a;
}

.ppm-side {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ppm-phase-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 9px;
  border-radius: 20px;
  background: #eef1f6;
  color: #344767;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1;
}
.ppm-phase-badge .material-icons {
  font-size: 13px;
}
.ppm-check {
  color: #4caf50;
  font-size: 22px;
}
.ppm-chevron-btn {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #7b809a;
  transition: background 0.15s ease;
}
.ppm-chevron-btn:hover {
  background: #eef1f6;
}
.ppm-chevron-btn .material-icons {
  font-size: 22px;
  transition: transform 0.25s ease;
}
.ppm-chevron-btn.is-open .material-icons {
  transform: rotate(180deg);
}

/* Animated open/close using the grid-template-rows 0fr→1fr trick */
.ppm-phases-wrap {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s ease;
}
.ppm-phases-wrap.is-open {
  grid-template-rows: 1fr;
}
.ppm-phases {
  overflow: hidden;
  min-height: 0;
}
.ppm-phases-inner {
  border-top: 1px dashed #e9ecef;
  background: #fafbfc;
  /* Left padding lines the phase rows up with the project text (14px row padding
     + 40px avatar + 12px gap). Capped height with inner scroll so a long phase
     list can't push the footer out of reach. */
  padding: 8px 14px 10px 66px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 236px;
  overflow-y: auto;
}

/* "Register on the project" — deliberately styled unlike the phase rows so it
   reads as picking the project itself, not one of its phases. */
.ppm-whole-project {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 38px;
  padding: 5px 10px;
  margin-bottom: 4px;
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;
}
.ppm-whole-project:hover {
  border-color: #c8ccd4;
  background: #f6f8fb;
}
.ppm-whole-project.is-selected {
  border-color: #344767;
  background: #e3e9f2;
}
.ppm-whole-project .ppm-phase-name {
  font-weight: 600;
}
.ppm-wp-icon {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #344767;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ppm-wp-icon .material-icons {
  font-size: 14px;
}
.ppm-phase-divider {
  margin: 4px 0 2px 10px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #7b809a;
}
.ppm-phase {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 34px;
  padding: 5px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s ease;
}
.ppm-phase:hover {
  background: #eef1f6;
}
.ppm-phase.is-selected {
  background: #e3e9f2;
}
.ppm-phase-dot {
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #adb5bd;
}
.ppm-phase.is-selected .ppm-phase-dot {
  background: #344767;
}
.ppm-phase-name {
  flex: 1 1 auto;
  font-size: 0.8rem;
  color: #344767;
  word-break: break-word;
}
.ppm-phase-check {
  color: #4caf50;
  font-size: 18px;
}

.ppm-empty {
  text-align: center;
  padding: 28px 0;
  color: #7b809a;
}
.ppm-empty .material-icons {
  font-size: 36px;
  opacity: 0.5;
}
.ppm-empty p {
  margin: 6px 0 0;
  font-size: 0.85rem;
}

.ppm-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #f0f2f5;
  /* Keep the confirm button reachable while an expanded phases drawer makes the
     modal taller than the viewport. */
  position: sticky;
  bottom: 0;
  background: #fff;
  padding-bottom: 4px;
}
.ppm-pager {
  display: flex;
  align-items: center;
  gap: 6px;
}
.ppm-pager-btn {
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 8px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #344767;
  padding: 0;
}
.ppm-pager-btn:disabled {
  opacity: 0.35;
  cursor: default;
}
.ppm-pager-btn .material-icons {
  font-size: 18px;
}
.ppm-pager-label {
  font-size: 0.75rem;
  color: #7b809a;
  white-space: nowrap;
}

/* Loading skeletons */
.ppm-skeleton {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px 10px 14px;
  min-height: 60px;
  border: 1px solid #e9ecef;
  border-radius: 12px;
}
.ppm-skeleton .ppm-avatar {
  background: none;
}
.ppm-skeleton-lines {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ppm-skeleton-block {
  background: linear-gradient(90deg, #f0f1f3 25%, #e6e8eb 50%, #f0f1f3 75%);
  background-size: 200% 100%;
  animation: ppm-shimmer 1.2s infinite;
  border-radius: 6px;
}
.ppm-skeleton-line {
  height: 10px;
  width: 70%;
}
.ppm-skeleton-line.short {
  width: 35%;
}
@keyframes ppm-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Dark mode: rows sit on the sunken surface instead of white; selection uses the brand accent */
body.dark-version .ppm-subtitle,
body.dark-version .ppm-name,
body.dark-version .ppm-picked-phase .material-icons,
body.dark-version .ppm-chevron-btn,
body.dark-version .ppm-phase-divider,
body.dark-version .ppm-empty,
body.dark-version .ppm-pager-label {
  color: var(--ts-text-muted);
}
body.dark-version .ppm-key,
body.dark-version .ppm-picked-phase {
  color: var(--ts-text-strong);
}
body.dark-version .ppm-phase-name {
  color: var(--ts-text);
}
body.dark-version .ppm-item,
body.dark-version .ppm-skeleton {
  background: var(--ts-surface-sunken);
  border-color: var(--ts-divider);
}
body.dark-version .ppm-item:hover {
  border-color: var(--ts-border);
  box-shadow: none;
}
body.dark-version .ppm-item.is-selected {
  border-color: var(--ts-accent);
  box-shadow: 0 0 0 1px var(--ts-accent);
}
body.dark-version .ppm-avatar,
body.dark-version .ppm-wp-icon {
  background: var(--ts-surface-raised);
  color: var(--ts-text);
}
body.dark-version .ppm-item.is-selected .ppm-avatar {
  background: var(--ts-accent);
  color: var(--ts-on-accent);
}
body.dark-version .ppm-phase-badge,
body.dark-version .ppm-chevron-btn:hover,
body.dark-version .ppm-phase:hover,
body.dark-version .ppm-phase.is-selected,
body.dark-version .ppm-whole-project:hover {
  background: var(--ts-surface-raised);
}
body.dark-version .ppm-phase-badge {
  color: var(--ts-text);
}
body.dark-version .ppm-phases-inner {
  background: var(--ts-surface);
  border-top-color: var(--ts-divider);
}
body.dark-version .ppm-whole-project {
  background: var(--ts-surface-sunken);
  border-color: var(--ts-divider);
}
body.dark-version .ppm-whole-project.is-selected {
  background: var(--ts-surface-raised);
  border-color: var(--ts-accent);
}
body.dark-version .ppm-phase.is-selected .ppm-phase-dot {
  background: var(--ts-accent);
}
body.dark-version .ppm-footer {
  background: var(--ts-surface);
  border-top-color: var(--ts-divider);
}
body.dark-version .ppm-pager-btn {
  background: var(--ts-surface-sunken);
  border-color: var(--ts-border);
  color: var(--ts-text);
}
body.dark-version .ppm-skeleton-block {
  background: linear-gradient(90deg, #262f48 25%, #2f3954 50%, #262f48 75%);
  background-size: 200% 100%;
}

/* iOS Safari auto-zooms the viewport when a focused input's font-size is under
   16px — keep the search field at 16px on phones so tapping it doesn't zoom. */
@media (max-width: 767px) {
  .search-input :deep(.el-input__inner) {
    font-size: 16px;
  }
}
</style>
